import { sectionRenderer } from "@theme/section-renderer";
import { Component } from "@theme/component";
import { FilterUpdateEvent, ThemeEvents } from "@theme/events";
import { debounce, formatMoney, startViewTransition } from "@theme/utilities";

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = "q";

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ["facetsForm"];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get("filter.v.price.gte") === "")
      newParameters.delete("filter.v.price.gte");
    if (newParameters.get("filter.v.price.lte") === "")
      newParameters.delete("filter.v.price.lte");

    newParameters.delete("page");

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? "";
  }

  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id) throw new Error("Section ID is required");
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = "";
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState(
      { urlParameters: urlParameters.toString() },
      "",
      url.toString(),
    );
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest("dialog");

    if (viewTransition) {
      startViewTransition(
        () => sectionRenderer.renderSection(this.sectionId),
        ["product-grid"],
      );
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState("", "", url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get("facets-form-component")) {
  customElements.define("facets-form-component", FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest(".shopify-section")?.id;
    if (!id)
      throw new Error("FacetInputs component must be a child of a section");
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest("facets-form-component");

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector("input");

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest("form");
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector("input");

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked)
      formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters)
      url.searchParams.append(key, value);

    if (inputElement.checked)
      url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter(
      (input) => input.checked,
    );
    const details = this.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get("facet-inputs-component")) {
  customElements.define("facet-inputs-component", FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === "") return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute("data-min") ?? ""));
    const max = Number(formatMoney(input.getAttribute("data-max") ?? ""));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", "0");
    if (maxInput.value === "")
      minInput.setAttribute(
        "data-max",
        maxInput.getAttribute("data-max") ?? "",
      );
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get("price-facet-component")) {
  customElements.define("price-facet-component", PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ["clearButton"];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keyup", this.#handleKeyUp);
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest(
      "facet-inputs-component, price-facet-component",
    );
    container
      ?.querySelectorAll('[type="checkbox"]:checked, input')
      .forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = "";
        }
      });

    const details = event.target.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === "Enter") this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle(
        "facets__clear--active",
        event.shouldShowClearAll(),
      );
    }
  };
}

if (!customElements.get("facet-clear-component")) {
  customElements.define("facet-clear-component", FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form
      ? document.getElementById(form)
      : this.closest("facets-form-component");

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle("active", event.shouldShowClearAll());
    }
  };
}

if (!customElements.get("facet-remove-component")) {
  customElements.define("facet-remove-component", FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ["details", "summary", "listbox"];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find(
      (option) => option instanceof HTMLElement && option.tabIndex === 0,
    );
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case "ArrowUp":
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case "Enter":
      case " ":
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case "Escape":
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (
      !(details instanceof HTMLDetailsElement) ||
      !(summary instanceof HTMLElement)
    )
      return;

    const isOpen = details.open;
    summary.setAttribute("aria-expanded", isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute("aria-selected", "false");
      });
      option.setAttribute("aria-selected", "true");

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    let facetsForm = this.closest("facets-form-component");
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLSelectElement
    ) {
      facetsForm =
        event.target.form?.closest("facets-form-component") || facetsForm;
    }
    if (!(facetsForm instanceof FacetsFormComponent)) {
      const foundForm = this.closest(".shopify-section")?.querySelector(
        "facets-form-component",
      );
      if (foundForm instanceof FacetsFormComponent) {
        facetsForm = foundForm;
      }
    }

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldUseSelectOnMobile =
      this.dataset.shouldUseSelectOnMobile === "true";
    const shouldUseSelectOnDesktop =
      this.dataset.shouldUseSelectOnDesktop === "true";

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldUseSelectOnMobile) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    if (shouldUseSelectOnDesktop) {
      if (isMobile) {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      } else {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldUseSelectOnMobile) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    if (shouldUseSelectOnDesktop) {
      if (isMobile) {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      } else {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector("details");
    if (!details) return;

    const facetStatus = details.querySelector("facet-status-component");
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy
        ? (event.target.dataset.optionName ?? "")
        : "";
  }
}

if (!customElements.get("sorting-filter-component")) {
  customElements.define("sorting-filter-component", SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute("facet-type") === "swatches"
      ? this.#updateSwatchSummary(
          checkedInputElements,
          checkedInputElementsCount,
        )
      : this.#updateBubbleSummary(
          checkedInputElements,
          checkedInputElementsCount,
        );
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove("bubble", "facets__bubble");

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = "";
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add("bubble", "facets__bubble");
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector("span.swatch");
        return swatch?.outerHTML ?? "";
      })
      .join("");
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove("bubble", "facets__bubble");

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = "";
      return;
    }

    if (filterStyle === "horizontal" && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? "";
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add("bubble", "facets__bubble");
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = "";
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, "0");
    const maxInputNum = this.#parseCents(
      maxInputValue,
      facetStatus.dataset.rangeMax,
    );
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = "0") {
    const parts = value
      ? value.trim().split(/[^0-9]/)
      : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== "string" || rest.length > 0)
      return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || "0", 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return "";

    const template = this.refs.moneyFormat.content.textContent || "{{amount}}";
    const currency = this.refs.facetStatus.dataset.currency || "";

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== "string") return "";
      if (placeholder === "currency") return currency;

      let thousandsSeparator = ",";
      let decimalSeparator = ".";
      let precision =
        CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === "amount") {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === "amount_no_decimals") {
        precision = 0;
      } else if (placeholder === "amount_with_comma_separator") {
        thousandsSeparator = ".";
        decimalSeparator = ",";
      } else if (placeholder === "amount_no_decimals_with_comma_separator") {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = ".";
        precision = 0;
      } else if (placeholder === "amount_no_decimals_with_space_separator") {
        thousandsSeparator = " ";
        precision = 0;
      } else if (placeholder === "amount_with_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ",";
      } else if (placeholder === "amount_with_period_and_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ".";
      } else if (placeholder === "amount_with_apostrophe_separator") {
        thousandsSeparator = "'";
        decimalSeparator = ".";
      }

      return this.#formatCents(
        moneyValue,
        thousandsSeparator,
        decimalSeparator,
        precision,
      );
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split(".");
    if (!a) a = "0";
    if (!b) b = "";

    // Split by groups of 3 digits
    a = a.replace(
      /\d(?=(\d\d\d)+(?!\d))/g,
      (digit) => digit + thousandsSeparator,
    );

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, "0");
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = "";
  }
}

if (!customElements.get("facet-status-component")) {
  customElements.define("facet-status-component", FacetStatusComponent);
}

/**
 * Handles price range slider functionality
 * @extends {Component}
 */
class PriceRangeSliderComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Wait for next frame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      this.#init();
    });
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate,
    );
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  /**
   * Initializes the range slider
   */
  #init() {
    const sliderOne = this.querySelector(".range-slider__input--min");
    const sliderTwo = this.querySelector(".range-slider__input--max");
    const handleLabelOne = this.querySelector(".range-slider__handle-label--min");
    const handleLabelTwo = this.querySelector(".range-slider__handle-label--max");
    const sliderTrack = this.querySelector(".range-slider__track");
    const container = this.querySelector(".range-slider__container");

    if (
      !(sliderOne instanceof HTMLInputElement) ||
      !(sliderTwo instanceof HTMLInputElement) ||
      !handleLabelOne ||
      !handleLabelTwo ||
      !sliderTrack ||
      !container
    ) {
      // Retry if elements aren't ready yet
      setTimeout(() => this.#init(), 50);
      return;
    }

    // Find the price-facet-component in the same container
    const priceFacetComponent = this.closest(
      ".facets__panel-content",
    )?.querySelector("price-facet-component");
    if (!(priceFacetComponent instanceof PriceFacetComponent)) {
      // Retry if price-facet-component isn't ready yet
      setTimeout(() => this.#init(), 50);
      return;
    }

    const maxPrice = parseInt(this.dataset.maxPrice || "0");
    const defaultFormat = "{" + "{" + "amount" + "}" + "}";
    const moneyFormat = this.dataset.moneyFormat || defaultFormat;
    const currency = this.dataset.currency || "";
    const minGap = 0;
    const sliderMaxValue = maxPrice;

    // Store references
    this.#sliderOne = sliderOne;
    this.#sliderTwo = sliderTwo;
    this.#handleLabelOne =
      handleLabelOne instanceof HTMLElement ? handleLabelOne : undefined;
    this.#handleLabelTwo =
      handleLabelTwo instanceof HTMLElement ? handleLabelTwo : undefined;
    const handleLabelValueOneEl = handleLabelOne?.querySelector(
      ".range-slider__handle-label-value",
    );
    const handleLabelValueTwoEl = handleLabelTwo?.querySelector(
      ".range-slider__handle-label-value",
    );
    this.#handleLabelValueOne =
      handleLabelValueOneEl instanceof HTMLElement
        ? handleLabelValueOneEl
        : undefined;
    this.#handleLabelValueTwo =
      handleLabelValueTwoEl instanceof HTMLElement
        ? handleLabelValueTwoEl
        : undefined;
    this.#sliderTrack =
      sliderTrack instanceof HTMLElement ? sliderTrack : undefined;
    this.#container = container instanceof HTMLElement ? container : undefined;
    this.#priceFacetComponent = priceFacetComponent;
    this.#maxPrice = maxPrice;
    this.#moneyFormat = moneyFormat;
    this.#currency = currency;
    this.#sliderMaxValue = sliderMaxValue;
    this.#minGap = minGap;

    // Debounce timer
    this.#debounceTimer = null;

    // Initialize sliders
    this.#initializeSliders();

    // Attach event listeners
    sliderOne.addEventListener("input", this.#slideOne.bind(this));
    sliderTwo.addEventListener("input", this.#slideTwo.bind(this));
    sliderOne.addEventListener("change", this.#updatePriceFilter.bind(this));
    sliderTwo.addEventListener("change", this.#updatePriceFilter.bind(this));

    if (this.#container) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#positionHandleLabel("min");
        this.#positionHandleLabel("max");
      });
      this.#resizeObserver.observe(this.#container);
    }

    // Sync sliders when text inputs change
    this.#attachInputListeners();

    // Listen for DOM mutations
    this.#setupMutationObserver();
  }

  /** @type {HTMLInputElement | undefined} */
  #sliderOne;
  /** @type {HTMLInputElement | undefined} */
  #sliderTwo;
  /** @type {HTMLElement | undefined} */
  #handleLabelOne;
  /** @type {HTMLElement | undefined} */
  #handleLabelTwo;
  /** @type {HTMLElement | undefined} */
  #handleLabelValueOne;
  /** @type {HTMLElement | undefined} */
  #handleLabelValueTwo;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {HTMLElement | undefined} */
  #sliderTrack;
  /** @type {HTMLElement | undefined} */
  #container;
  /** @type {PriceFacetComponent | undefined} */
  #priceFacetComponent;
  /** @type {number} */
  #maxPrice = 0;
  /** @type {string} */
  #moneyFormat = "";
  /** @type {string} */
  #currency = "";
  /** @type {number} */
  #sliderMaxValue = 0;
  /** @type {number} */
  #minGap = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;

  /**
   * Formats money value (in cents) to formatted string
   * @param {number} moneyValue - The money value in cents
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!this.#moneyFormat) return "";

    const template = this.#moneyFormat;
    const currency = this.#currency;

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
      if (typeof placeholder !== "string") return "";
      if (placeholder === "currency") return currency || "";

      let thousandsSeparator = ",";
      let decimalSeparator = ".";
      let precision =
        CURRENCY_DECIMALS[currency?.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === "amount") {
        // Use defaults
      } else if (placeholder === "amount_no_decimals") {
        precision = 0;
      } else if (placeholder === "amount_with_comma_separator") {
        thousandsSeparator = ".";
        decimalSeparator = ",";
      } else if (placeholder === "amount_no_decimals_with_comma_separator") {
        thousandsSeparator = ".";
        precision = 0;
      } else if (placeholder === "amount_no_decimals_with_space_separator") {
        thousandsSeparator = " ";
        precision = 0;
      } else if (placeholder === "amount_with_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ",";
      } else if (placeholder === "amount_with_period_and_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ".";
      } else if (placeholder === "amount_with_apostrophe_separator") {
        thousandsSeparator = "'";
        decimalSeparator = ".";
      }

      return this.#formatCents(
        moneyValue,
        thousandsSeparator,
        decimalSeparator,
        precision,
      );
    });
  }

  /**
   * Formats cents to money string
   * @param {number} moneyValue - The money value in cents
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);
    let [a, b] = roundedNumber.split(".");
    if (!a) a = "0";
    if (!b) b = "";

    // Split by groups of 3 digits
    a = a.replace(
      /\d(?=(\d\d\d)+(?!\d))/g,
      (digit) => digit + thousandsSeparator,
    );

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, "0");
  }

  /**
   * Converts cents to decimal format for inputs
   * @param {number} cents - The value in cents
   * @returns {string} The decimal representation
   */
  #centsToDecimal(cents) {
    return (cents / 100).toFixed(2);
  }

  /**
   * Positions a handle value label under the corresponding thumb
   * @param {'min' | 'max'} slider - Which slider to position the label for
   */
  #positionHandleLabel(slider) {
    const sliderInput = slider === "min" ? this.#sliderOne : this.#sliderTwo;
    const label = slider === "min" ? this.#handleLabelOne : this.#handleLabelTwo;

    if (!sliderInput || !label || !this.#container) return;

    const min = Number(sliderInput.min || "0");
    const max = Number(sliderInput.max || this.#sliderMaxValue);
    const value = Number(sliderInput.value);
    const range = max - min || 1;
    const progress = (value - min) / range;
    const thumbSize = 20;
    const containerWidth = this.#container.offsetWidth;
    const usableWidth = Math.max(containerWidth - thumbSize, 0);
    const position = progress * usableWidth + thumbSize / 2;
    const clampedPosition = Math.max(
      thumbSize / 2,
      Math.min(position, containerWidth - thumbSize / 2),
    );

    label.style.left = `${clampedPosition}px`;
    label.style.transform = "translateX(-50%)";
  }

  /**
   * Debounce helper
   * @param {Function} func - The function to debounce
   * @param {number} wait - The wait time in milliseconds
   * @returns {Function} The debounced function
   */
  #debounce(func, wait) {
    return (/** @type {any[]} */ ...args) => {
      if (this.#debounceTimer) {
        clearTimeout(this.#debounceTimer);
      }
      this.#debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Updates price filter inputs and trigger filtering
   */
  #updatePriceFilter = () => {
    if (!this.#priceFacetComponent?.refs) return;

    const minInput = this.#priceFacetComponent.refs.minInput;
    const maxInput = this.#priceFacetComponent.refs.maxInput;

    if (minInput && maxInput && this.#sliderOne && this.#sliderTwo) {
      const minCents = parseInt(this.#sliderOne.value);
      const maxCents = parseInt(this.#sliderTwo.value);

      // Convert cents to decimal and update inputs
      minInput.value = minCents === 0 ? "" : this.#centsToDecimal(minCents);
      maxInput.value =
        maxCents === this.#maxPrice ? "" : this.#centsToDecimal(maxCents);

      // Trigger the price filter update
      if (this.#priceFacetComponent.updatePriceFilterAndResults) {
        this.#priceFacetComponent.updatePriceFilterAndResults();
      }
    }
  };

  /**
   * Debounced version of updatePriceFilter
   */
  #debouncedUpdatePriceFilter = this.#debounce(this.#updatePriceFilter, 300);

  /**
   * Updates display only (without triggering filter)
   */
  #updateDisplayOnly() {
    if (!this.#sliderOne || !this.#sliderTwo) return;

    const formattedValue1 = this.#formatMoney(parseInt(this.#sliderOne.value));
    const formattedValue2 = this.#formatMoney(parseInt(this.#sliderTwo.value));

    if (this.#handleLabelValueOne) {
      this.#handleLabelValueOne.textContent = formattedValue1;
    }
    if (this.#handleLabelValueTwo) {
      this.#handleLabelValueTwo.textContent = formattedValue2;
    }

    this.#positionHandleLabel("min");
    this.#positionHandleLabel("max");

    this.#fillColor();
  }

  /**
   * Updates the position of text labels (no longer needed as labels are fixed)
   */
  #updateInputPositions() {
    // Labels are now fixed at start and end, no positioning needed
    return;
  }

  /**
   * Parses decimal number to cents (handles European format like "2.349,60")
   * @param {string} value - The string value to parse
   * @param {string} fallback - The fallback value
   * @returns {number} The value in cents
   */
  #parseCents(value, fallback = "0") {
    if (!value || !value.trim()) {
      return parseInt(fallback, 10);
    }

    const trimmed = value.trim();
    let wholeStr = "";
    let fractionStr = "";

    // Find the last non-numeric separator
    const lastSeparatorIndex = Math.max(
      trimmed.lastIndexOf("."),
      trimmed.lastIndexOf(","),
    );

    if (lastSeparatorIndex > -1) {
      // Has a decimal separator
      wholeStr = trimmed
        .substring(0, lastSeparatorIndex)
        .replace(/[^0-9]/g, "");
      fractionStr = trimmed
        .substring(lastSeparatorIndex + 1)
        .replace(/[^0-9]/g, "");
    } else {
      // No decimal separator, treat entire string as whole number
      wholeStr = trimmed.replace(/[^0-9]/g, "");
      fractionStr = "0";
    }

    if (!wholeStr) {
      return parseInt(fallback, 10);
    }

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || "0", 10);

    // Use two most-significant digits
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Syncs sliders from text inputs (two-way binding)
   */
  #syncSlidersFromInputs = () => {
    if (!this.#priceFacetComponent?.refs) return;

    const minInput = this.#priceFacetComponent.refs.minInput;
    const maxInput = this.#priceFacetComponent.refs.maxInput;

    if (minInput && maxInput && this.#sliderOne && this.#sliderTwo) {
      const minValue = minInput.value.trim();
      const maxValue = maxInput.value.trim();

      if (minValue) {
        const minCents = this.#parseCents(minValue, "0");
        if (minCents >= 0 && minCents <= this.#maxPrice) {
          this.#sliderOne.value = minCents.toString();
        } else {
          this.#sliderOne.value = "0";
        }
      } else {
        this.#sliderOne.value = "0";
      }

      if (maxValue) {
        const maxCents = this.#parseCents(maxValue, this.#maxPrice.toString());
        if (maxCents >= 0 && maxCents <= this.#maxPrice) {
          this.#sliderTwo.value = maxCents.toString();
        } else {
          this.#sliderTwo.value = this.#maxPrice.toString();
        }
      } else {
        this.#sliderTwo.value = this.#maxPrice.toString();
      }

      // Ensure sliders don't cross over
      if (parseInt(this.#sliderOne.value) > parseInt(this.#sliderTwo.value)) {
        this.#sliderOne.value = this.#sliderTwo.value;
      }

      // Update display and fill color
      this.#updateDisplayOnly();
      requestAnimationFrame(() => {
        this.#fillColor();
      });
    }
  };

  /**
   * Handles slider one movement
   */
  #slideOne = () => {
    if (!this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value);
    const val2 = parseInt(this.#sliderTwo.value);

    if (val2 - val1 <= this.#minGap) {
      this.#sliderOne.value = (val2 - this.#minGap).toString();
    }

    this.#updateDisplayOnly();
    this.#debouncedUpdatePriceFilter();
  };

  /**
   * Handles slider two movement
   */
  #slideTwo = () => {
    if (!this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value);
    const val2 = parseInt(this.#sliderTwo.value);

    if (val2 - val1 <= this.#minGap) {
      this.#sliderTwo.value = (val1 + this.#minGap).toString();
    }

    this.#updateDisplayOnly();
    this.#debouncedUpdatePriceFilter();
  };

  /**
   * Fills the colored track between sliders
   */
  #fillColor() {
    if (!this.#sliderTrack || !this.#sliderOne || !this.#sliderTwo) return;

    const val1 = parseInt(this.#sliderOne.value) || 0;
    const val2 = parseInt(this.#sliderTwo.value) || this.#sliderMaxValue;

    // Ensure values are valid
    const minVal = Math.max(0, Math.min(val1, this.#sliderMaxValue));
    const maxVal = Math.max(minVal, Math.min(val2, this.#sliderMaxValue));

    const percent1 = (minVal / this.#sliderMaxValue) * 100;
    const percent2 = (maxVal / this.#sliderMaxValue) * 100;

    // Ensure the gradient is always visible
    const startPercent = Math.min(percent1, percent2);
    const endPercent = Math.max(percent1, percent2);

    this.#sliderTrack.style.background = `linear-gradient(to right, #dadae5 ${startPercent}% , var(--color-foreground, #3264fe) ${startPercent}% , var(--color-foreground, #3264fe) ${endPercent}%, #dadae5 ${endPercent}%)`;
  }

  /**
   * Initializes sliders from current input values
   */
  #initializeSliders() {
    if (
      this.#priceFacetComponent?.refs?.minInput &&
      this.#priceFacetComponent.refs?.maxInput
    ) {
      this.#syncSlidersFromInputs();
      requestAnimationFrame(() => {
        this.#updateDisplayOnly();
      });
    } else {
      // Retry after a short delay if refs aren't ready yet
      setTimeout(() => this.#initializeSliders(), 50);
    }
  }

  /**
   * Attaches listeners to text inputs
   */
  #attachInputListeners() {
    const minInput = this.#priceFacetComponent?.refs?.minInput;
    const maxInput = this.#priceFacetComponent?.refs?.maxInput;

    if (minInput && maxInput) {
      minInput.addEventListener("input", this.#syncSlidersFromInputs);
      minInput.addEventListener("change", this.#syncSlidersFromInputs);
      maxInput.addEventListener("input", this.#syncSlidersFromInputs);
      maxInput.addEventListener("change", this.#syncSlidersFromInputs);
    } else {
      // Retry after a short delay if refs aren't ready yet
      setTimeout(() => this.#attachInputListeners(), 50);
    }
  }

  /**
   * Sets up mutation observer for section re-renders
   */
  #setupMutationObserver() {
    const panelContent = this.closest(".facets__panel-content");
    if (!panelContent) return;

    this.#observer = new MutationObserver((mutations) => {
      let shouldResync = false;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          Array.from(mutation.addedNodes).forEach((node) => {
            if (node.nodeType === 1 && node instanceof Element) {
              if (
                node.matches("price-facet-component") ||
                node.querySelector("price-facet-component")
              ) {
                shouldResync = true;
              }
            }
          });
        }
      });
      if (shouldResync) {
        this.#reSyncAfterUpdate();
      }
    });

    this.#observer.observe(panelContent, { childList: true, subtree: true });
  }

  /**
   * Re-syncs after filter updates
   */
  #reSyncAfterUpdate = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (
          this.#priceFacetComponent?.refs?.minInput &&
          this.#priceFacetComponent.refs?.maxInput
        ) {
          this.#syncSlidersFromInputs();
          requestAnimationFrame(() => {
            this.#updateDisplayOnly();
          });
        }
      }, 100);
    });
  };

  /**
   * Handles filter update events
   */
  #handleFilterUpdate = () => {
    this.#reSyncAfterUpdate();
  };
}

if (!customElements.get("price-range-slider-component")) {
  customElements.define(
    "price-range-slider-component",
    PriceRangeSliderComponent,
  );
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
